"use client";

import { useCallback, useRef } from 'react';
import { useAuth } from '@/lib/auth/auth-context';

interface AnalyticsEvent {
  podcastId: number;
  sessionId: string;
  eventType: 'play_start' | 'play_progress' | 'play_end' | 'play_pause';
  currentTime?: number;
  duration?: number;
  playSource?: string;
}

interface UseAnalyticsReturn {
  trackPlayStart: (podcastId: number, duration: number, playSource?: string) => void;
  trackPlayProgress: (currentTime: number, duration: number) => void;
  trackPlayEnd: (currentTime: number, duration: number) => void;
  trackPlayPause: (currentTime: number, duration: number) => void;
  getCurrentSessionId: () => string;
}

export function useAnalytics(): UseAnalyticsReturn {
  const { user } = useAuth();
  const sessionIdRef = useRef<string | null>(null);
  const currentPodcastIdRef = useRef<number | null>(null);
  const lastProgressUpdateRef = useRef<number>(0);

  // Generate or get session ID
  const getCurrentSessionId = useCallback(() => {
    if (!sessionIdRef.current) {
      sessionIdRef.current = crypto.randomUUID();
    }
    return sessionIdRef.current;
  }, []);

  // Send analytics event to API
  const sendAnalyticsEvent = useCallback(async (event: AnalyticsEvent) => {
    try {
      const response = await fetch('/api/analytics/track', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...event,
          userId: user?.id,
          userAgent: navigator.userAgent,
          referrer: document.referrer || window.location.origin,
        }),
      });

      if (!response.ok) {
        console.warn('⚠️ Falha no tracking de analytics:', response.statusText);
      }
    } catch (error) {
      console.warn('⚠️ Erro no tracking de analytics:', error);
      // Fail silently - analytics shouldn't break the user experience
    }
  }, [user?.id]);

  // Track play start
  const trackPlayStart = useCallback((
    podcastId: number, 
    duration: number, 
    playSource: string = 'unknown'
  ) => {
    // Generate new session ID for new podcast or if no current session
    if (currentPodcastIdRef.current !== podcastId) {
      sessionIdRef.current = crypto.randomUUID();
      currentPodcastIdRef.current = podcastId;
    }

    const sessionId = getCurrentSessionId();

    console.log(`📊 Tracking play start: podcast ${podcastId}, session ${sessionId.substring(0, 8)}...`);

    sendAnalyticsEvent({
      podcastId,
      sessionId,
      eventType: 'play_start',
      duration,
      playSource,
    });

    // Reset progress tracking
    lastProgressUpdateRef.current = 0;
  }, [getCurrentSessionId, sendAnalyticsEvent]);

  // Track play progress (throttled to avoid too many requests)
  const trackPlayProgress = useCallback((currentTime: number, duration: number) => {
    const sessionId = getCurrentSessionId();
    const currentPodcastId = currentPodcastIdRef.current;

    if (!currentPodcastId) {
      console.warn('⚠️ Tentativa de tracking de progresso sem podcast ativo');
      return;
    }

    // Throttle progress updates to every 15 seconds or significant progress jumps
    const timeSinceLastUpdate = currentTime - lastProgressUpdateRef.current;
    const shouldUpdate = timeSinceLastUpdate >= 15 || Math.abs(timeSinceLastUpdate) > 30;

    if (shouldUpdate) {
      sendAnalyticsEvent({
        podcastId: currentPodcastId,
        sessionId,
        eventType: 'play_progress',
        currentTime,
        duration,
      });

      lastProgressUpdateRef.current = currentTime;
    }
  }, [getCurrentSessionId, sendAnalyticsEvent]);

  // Track play end
  const trackPlayEnd = useCallback((currentTime: number, duration: number) => {
    const sessionId = getCurrentSessionId();
    const currentPodcastId = currentPodcastIdRef.current;

    if (!currentPodcastId) {
      console.warn('⚠️ Tentativa de tracking de fim sem podcast ativo');
      return;
    }

    console.log(`📊 Tracking play end: podcast ${currentPodcastId}, time ${Math.round(currentTime)}s`);

    sendAnalyticsEvent({
      podcastId: currentPodcastId,
      sessionId,
      eventType: 'play_end',
      currentTime,
      duration,
    });

    // Reset session for next play
    sessionIdRef.current = null;
    currentPodcastIdRef.current = null;
    lastProgressUpdateRef.current = 0;
  }, [getCurrentSessionId, sendAnalyticsEvent]);

  // Track play pause
  const trackPlayPause = useCallback((currentTime: number, duration: number) => {
    const sessionId = getCurrentSessionId();
    const currentPodcastId = currentPodcastIdRef.current;

    if (!currentPodcastId) {
      console.warn('⚠️ Tentativa de tracking de pausa sem podcast ativo');
      return;
    }

    sendAnalyticsEvent({
      podcastId: currentPodcastId,
      sessionId,
      eventType: 'play_pause',
      currentTime,
      duration,
    });
  }, [getCurrentSessionId, sendAnalyticsEvent]);

  return {
    trackPlayStart,
    trackPlayProgress,
    trackPlayEnd,
    trackPlayPause,
    getCurrentSessionId,
  };
}

// Hook for fetching podcast analytics
export function usePodcastAnalytics(podcastId: number | null) {
  const fetchAnalytics = useCallback(async () => {
    if (!podcastId) return null;

    try {
      const response = await fetch(`/api/analytics/podcast/${podcastId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.success ? result.data : null;
    } catch (error) {
      console.error('❌ Erro ao buscar analytics:', error);
      return null;
    }
  }, [podcastId]);

  return { fetchAnalytics };
}

// Utility function to determine play source
export function getPlaySource(): string {
  const pathname = window.location.pathname;
  
  if (pathname.includes('/dashboard')) {
    return 'dashboard';
  } else if (pathname.includes('/podcast/')) {
    return 'public_link';
  } else if (document.referrer && !document.referrer.includes(window.location.origin)) {
    return 'external_link';
  } else {
    return 'direct';
  }
}
